import React, { useState, useEffect, useMemo, useContext } from "react";
import "ol/ol.css"; //스타일
import { Feature, Map as OlMap, View } from "ol"; //뷰 관리
import { get as getProjection } from "ol/proj"; //위경도
import { Vector, XYZ } from "ol/source"; //지도정보
import { Tile as TileLayer } from "ol/layer";
import { Style, Text, Fill, Stroke, Circle, Icon } from "ol/style";
import VectorLayer from "ol/layer/Vector";
import { Button, ButtonGroup } from "@mui/material";
import { PropagateLoader } from "react-spinners";
import axios from "axios";
import { Point } from "ol/geom";
import DataContext, { urlToggle } from "../../ContextServer/DataContext";
import pinsvg from "./pin.svg";
import { enShowValue } from "./ButtonAvr";

export const enShowMap = {
  ShowMidnight: 0,
  ShowBase: 1,
  ShowStellite: 2,
};

let tileLayer = new TileLayer({
  source: new XYZ({
    url: "http://api.vworld.kr/req/wmts/1.0.0/681740AD-AFA7-4D08-9221-A506E4AF246F/midnight/{z}/{y}/{x}.png",
  }),
});

let hybridLayer = new TileLayer({
  source: new XYZ({
    url: `http://api.vworld.kr/req/wmts/1.0.0/681740AD-AFA7-4D08-9221-A506E4AF246F/Hybrid/{z}/{y}/{x}.png`,
  }),
});

const override = {
  display: "flex",
  margin: "0 auto",
  textAlign: "center",
};

function Map(props) {
  const [mapObject, setMapObject] = useState(null);
  const [mapType, setMapType] = useState("midnight");

  const { setStinkNode, selectedSensor, stinkMapData, odorReports } =
    useContext(DataContext);
  const [loading, setLoading] = useState(true);

  const onClick = (value) => {
    setMapType(value);
    UpdateMapType(value);
  };

  const poiLayer = useMemo(() => {
    let markerStyle = new Style({
      fill: new Fill({
        color: "rgba(255, 255, 255, 0.2)",
      }),
      stroke: new Stroke({
        color: "#3B11B2",
        width: 2,
      }),
      image: new Circle({
        radius: 11,
        fill: new Fill({
          color: "rgba(255, 255, 255, 0.4)",
        }),
        stroke: new Stroke({ color: "#3B11B2", width: 2 }),
      }),
    });
    let vectorLayer = new VectorLayer({
      source: new Vector({ wrapX: false }),
      style: markerStyle,
    });
    return vectorLayer;
  }, []);

  const markerLayer = useMemo(() => {
    let markerStyle = new Style({
      image: new Icon({
        //마커 이미지
        opacity: 1, //투명도 1=100%
        scale: 0.1, //크기 1=100%
        src: pinsvg,
      }),
      //html의 css, z-index 기능이다.
      zindex: 10,
    });

    let markerSource = new Vector();
    let markerLayer = new VectorLayer({
      source: markerSource, //마커 feacture들
      style: markerStyle, //마커 스타일
    });
    return markerLayer;
  }, []);
  // 항공지도(jpeg) 띄우기
  const UpdateMapType = (value) => {
    if (value === "base") {
      tileLayer
        .getSource()
        .setUrl(
          "http://api.vworld.kr/req/wmts/1.0.0/681740AD-AFA7-4D08-9221-A506E4AF246F/Base/{z}/{y}/{x}.png",
        );
    } else if (value === "aerial") {
      tileLayer
        .getSource()
        .setUrl(
          "http://api.vworld.kr/req/wmts/1.0.0/681740AD-AFA7-4D08-9221-A506E4AF246F/Satellite/{z}/{y}/{x}.jpeg",
        );
    } else {
      tileLayer
        .getSource()
        .setUrl(
          "http://api.vworld.kr/req/wmts/1.0.0/681740AD-AFA7-4D08-9221-A506E4AF246F/midnight/{z}/{y}/{x}.png",
        );
    }
    tileLayer.getSource().changed();
  };

  // View(기본 위치값)
  const view = new View({
    projection: getProjection("EPSG:3857"),
    //center: fromLonLat([127.0960, 36.9917], getProjection("EPSG:3857")),
    center: [14101791.57, 4161339.5], //동방회관
    zoom: 13,
  });

  useEffect(() => {
    // 악취
    if (props.hide === "false" && props.stinkToggle) {
      updateStinkMap();
    } else if (props.hide === "norm") {
      updateNormMap();
    }
  }, [props, selectedSensor, stinkMapData]);

  const updateStinkMap = () => {
    const stinkResult = stinkMapData;
    poiLayer.getSource().clear();

    // 로딩중 띄우기
    setLoading(false);

    stinkResult?.forEach((element, idx) => {
      element.items.forEach((el, id) => {
        if (el.sensor_name_kor === selectedSensor) {
          let pos = [Number(el.xpos), Number(el.ypos)];
          const pointFeature = new Feature({
            geometry: new Point(pos),
          });

          let circle = [];
          let type = el.grade;

          switch (type) {
            case "good":
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#5c8cdd" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
            case "notice":
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#65b24b" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
            case "attention":
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#e2d058" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
            case "warning":
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#ec7a7a" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
            case "over":
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#ec7a7a" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
            default:
              circle = new Circle({
                radius: 11,
                fill: new Fill({ color: "#5c8cdd" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              });
              break;
          }

          pointFeature.setStyle(
            new Style({
              image: circle,
              text: props.textToggle
                ? new Text({
                    text: el.pos_nm,
                    scale: 1.5,
                    textAlign: "center",
                    offsetY: 20,
                    fill: new Fill({
                      color: "#ffffff",
                    }),
                    stroke: new Stroke({
                      color: "#000000",
                      width: 1,
                    }),
                  })
                : null,
            }),
          );

          pointFeature.set("station_seqno", element.title);
          pointFeature.set("grade", el?.grade);
          pointFeature.set("xpos", el?.xpos);
          pointFeature.set("ypos", el?.ypos);

          poiLayer.getSource().addFeature(pointFeature);
        }
      });
    });
  };

  const updateNormMap = () => {
    let results = odorReports;
    setLoading(null);

    // 데이터가 없을 때 기본 지도 설정
    if (!results || results.length === 0) {
      view.setCenter([14106451, 4162330]);
      view.setZoom(13);
    } else {
      // 데이터가 있을 때 지점 표시
      poiLayer.getSource().clear();

      results.forEach((element, idx) => {
        let pos = [Number(element.xpos), Number(element.ypos)];
        const pointFeature = new Feature({
          geometry: new Point(pos),
        });
        let circle = null;
        circle = new Circle({
          radius: 10,
          fill: new Fill({ color: "#fa5726" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 }),
        });
        pointFeature.setStyle(
          new Style({
            image: circle,
            // text: new Text({
            //   text: element.report_addr, //신고장소
            //   scale: 1.5,
            //   textAlign: "center",
            //   offsetY: 20,
            //   fill: new Fill({
            //     color: "#ffffff",
            //   }),
            //   stroke: new Stroke({
            //     color: "#000000",
            //     width: 1,
            //   }),
            // }),
          }),
        );

        pointFeature.set("seqno", element.seqno);

        poiLayer.getSource().addFeature(pointFeature);
        if (results.length - 1 === idx) {
          mapObject?.map?.getView().fit(poiLayer.getSource().getExtent());
          mapObject?.map
            ?.getView()
            .setZoom(mapObject?.map.getView().getZoom() - 2);
        }
      });
    }
  };
  let map = undefined;
  useEffect(() => {
    if (mapObject === null) {
      // 지도 중복 생성 막음
      map = new OlMap({
        layers: [tileLayer, hybridLayer, poiLayer, markerLayer],
        target: "map",
        view: view,
      });
      map.on("singleclick", (evt) => {
        // console.log(evt.coordinate); // 좌표값

        const feature = map.forEachFeatureAtPixel(evt.pixel, function (feats) {
          return feats;
        });
        if (!feature) return;

        markerLayer.getSource().clear();

        const xpos = feature.values_.geometry.flatCoordinates[0];
        const ypos = feature.values_.geometry.flatCoordinates[1];

        // console.log("props.hide : " + props.hide);
        if (props.hide === "false") {
          let station_seqno = feature.get("station_seqno");
          setStinkNode(station_seqno);
        }
        if (props.hide === "norm") {
          let seqno = feature.get("seqno");
          // console.log("민원 클릭 : " + seqno);
          props.setSeqNo(seqno);
          props.hole();
        }
        const markerFeature = new Feature({
          geometry: new Point([xpos, ypos]), // 클릭한 위치를 기반으로 Point(점) 지오메트리 생성
        });
        markerFeature.setStyle(
          new Style({
            image: new Icon({
              opacity: 1,
              scale: 0.1,
              src: pinsvg,
            }),
            zIndex: 10, // 마커 레이어의 zIndex와 일치해야 합니다.
          }),
        );
        markerLayer.getSource().addFeature(markerFeature);
        map.getView().setCenter(evt.coordinate);
        return feature;
      });
      setMapObject(map);
      return () => {
        map.setTarget(null);
      };
    }
  }, []);
  return (
    <div className="mapContainer">
      <div className="loading">
        <PropagateLoader
          color="#fac426"
          loading={loading}
          size={40}
          cssOverride={override}
          className="BounceLoader"
        />
      </div>

      <div className="elevated">
        <Button
          value="base"
          className={
            props.showMapValue === enShowMap.ShowBase ? "click" : " basic"
          }
          onClick={() => {
            onClick("base");
            props.setShowMapValue(enShowMap.ShowBase);
          }}
        >
          기본
        </Button>
        <Button
          className={
            props.showMapValue === enShowMap.ShowMidnight ? "click" : " basic"
          }
          value="midnight"
          onClick={() => {
            onClick("midnight");
            props.setShowMapValue(enShowMap.ShowMidnight);
          }}
        >
          야간
        </Button>
        <Button
          value="aerial"
          className={
            props.showMapValue === enShowMap.ShowStellite ? "click" : " basic"
          }
          onClick={() => {
            onClick("aerial");
            props.setShowMapValue(enShowMap.ShowStellite);
          }}
        >
          항공
        </Button>
      </div>

      <div className="map">
        <div id="map" value={mapObject}></div>
      </div>
    </div>
  );
}

export default Map;
